import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';

import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import 'chartjs-adapter-date-fns'; // Required for time series scales
import { HourlyRainData } from '../models/hourly-rain-data';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { RainGauge } from '../models/rain-gauge';
import { WaterBody } from '../models/water-body';
import { WaterBodySelectionService } from '../waterbody-selection.service';
import { RainDataService } from './rain-chart.service';




@Component({
  selector: 'app-rain-chart',
  standalone: true,
  templateUrl: './rain-chart.component.html',
  styleUrls: ['./rain-chart.component.css'],
  imports: [BaseChartDirective, FormsModule, DecimalPipe]
})
export class RainChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;

  public barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allow canvas to resize based on container
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'MMM dd, HH:00',
          displayFormats: {
            hour: 'HH',
            day: 'MMM dd'
          }
        },
        title: {
          display: false
        },
        grid: {
          display: true,
          color: (context) => context.tick?.major ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.06)'
        },
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          stepSize: 1,
          major: { enabled: true },
          font: (context) => context.tick?.major
            ? { size: 11, weight: 'bold' }
            : { size: 8 },
          callback: function(value, index, ticks) {
            const ts = typeof value === 'number' ? value : Number(value);
            const d = new Date(ts);
            const h = d.getHours();
            if (ticks[index]?.major) {
              return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
            }
            return (h === 6 || h === 12 || h === 18) ? h.toString().padStart(2, '0') : '';
          }
        }
      },
      y: {
        min: 0,
        max: 1,
        title: {
          display: true,
          text: 'Rainfall (inches)',
          font: { weight: 'bold' }
        },
        ticks: {
          stepSize: 0.5
        },
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.12)'
        },
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            // Customize tooltip title to show full timestamp
            return context[0].label;
          },
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2) + ' in';
            }
            return label;
          }
        }
      }
    }
  };

  public barChartType: ChartType = 'bar';
  public barChartPlugins = []; // Can add plugins here if needed

  public barChartData: ChartConfiguration['data'] = {
    labels: [], // Will be filled with timestamps
    datasets: [
      {
        data: [],
        label: 'Hourly Rainfall',
        backgroundColor: 'rgba(54, 162, 235, 0.7)', // Blue bars
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(54, 162, 235, 0.9)',
        hoverBorderColor: 'rgba(54, 162, 235, 1)'
      }
    ]
  };

  private dataSubscription: Subscription | undefined;
  private selectionSubscription: Subscription | undefined;
  private latestSelectedWaterBody: WaterBody | null = null;
  public errorMessage: string | null = null;
  public isLoading: boolean = false;
    public gaugesLoading: boolean = false;
  public gaugesErrorMessage: string | null = null;

  public gauges: RainGauge[] = [];
  public selectedGaugeId: string | null = null;
  public totalRainfall: number = 0;

  constructor(
    private rainDataService: RainDataService,
    private selectionService: WaterBodySelectionService
  ) { } // Inject services

  ngOnInit(): void {
    this.selectionSubscription = this.selectionService.selectedWaterBody$.subscribe(wb => {
      this.latestSelectedWaterBody = wb;
      if (wb) {
        if (this.gauges.length > 0) {
          this.selectGaugeForWaterBody(wb);
        }
      } else {
        // If selection is cleared, reset to the first available gauge
        if (this.gauges.length > 0 && this.selectedGaugeId !== this.gauges[0].id) {
          this.selectedGaugeId = this.gauges[0].id;
          this.fetchRainData(this.selectedGaugeId);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.gaugesLoading = true;
    this.gaugesErrorMessage = null;
    this.rainDataService.getRainGauges().pipe(
      catchError(error => {
        console.error('Error fetching gauges:', error);
        this.gaugesErrorMessage = 'Failed to load the list of gauges.';
        return of([]);
      }),
      finalize(() => this.gaugesLoading = false)
    ).subscribe(gauges => {
      this.gauges = gauges;
      if (this.latestSelectedWaterBody) {
        this.selectGaugeForWaterBody(this.latestSelectedWaterBody);
      } else if (this.gauges.length > 0) {
        this.selectedGaugeId = this.gauges[0].id;
        this.fetchRainData(this.selectedGaugeId);
      }
    });
  }


  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.selectionSubscription) {
      this.selectionSubscription.unsubscribe();
    }
  }

   onGaugeChange(): void {
    if (this.selectedGaugeId) {
      this.fetchRainData(this.selectedGaugeId);
    }
  }

  private selectGaugeForWaterBody(wb: WaterBody): void {
    // Attempt to find a matching gauge by sensor id or rain gauge name/id
    const targetIdentifier = wb.sensor?.id || wb.rainGauge;
    if (!targetIdentifier) {
      return;
    }

    const matchingGauge = this.gauges.find(g => 
      g.id === targetIdentifier || g.name === targetIdentifier
    );

    if (matchingGauge && matchingGauge.id !== this.selectedGaugeId) {
      this.selectedGaugeId = matchingGauge.id;
      this.fetchRainData(this.selectedGaugeId);
    }
  }

  fetchRainData(sensorId: string): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.totalRainfall = 0;


    this.dataSubscription = this.rainDataService.getFourDayHourlyRainData(sensorId).pipe(
      map(data => {
        // Sort data by timestamp to ensure correct chronological order for the chart
      //  data.sort((a, b) => new Date(a.occurredOn).getTime() - new Date(b.occurredOn).getTime());

        const labels: string[] = [];
        const rainAmounts: number[] = [];

        data.forEach(item => {
          labels.push(item.occurredOn); // Chart.js time scale handles ISO strings
          rainAmounts.push(item.precipitation);
        });

        // Update chart data
        this.barChartData.labels = labels;
        this.barChartData.datasets[0].data = rainAmounts;
        
        // Calculate total rainfall for the last 24 hours only (most recent samples)
        const last24Hours = rainAmounts.slice().reverse().slice(-24);
        this.totalRainfall = last24Hours.reduce((sum, v) => sum + v, 0);

        // Ensure the chart updates
        if (this.chart) {
          this.chart.update();
        }
        return data;
      }),
      catchError(error => {
        console.error('Error fetching rain data:', error);
        this.errorMessage = 'Failed to load rain data. Please try again later.';
        return of([]); // Return an empty observable to continue the stream
      })
    ).subscribe(() => {
      this.isLoading = false;
    });
  }

  // --- Mock Data Generator ---
  private generateMockRainData(days: number): Observable<HourlyRainData[]> {
    const data: HourlyRainData[] = [];
    const now = new Date();
    // Start 4 days ago to have some past data
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days +1, 0, 0, 0);

    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        const timestamp = new Date(startDate);
        timestamp.setDate(startDate.getDate() + d);
        timestamp.setHours(h);

        // Simulate some random rain, with more likely zero
        let rainAmount = 0;
        if (Math.random() < 0.2) { // 20% chance of rain
          rainAmount = parseFloat((Math.random() * 5).toFixed(1)); // 0 to 5 mm, one decimal place
        }
        data.push({
          occurredOn: timestamp.toISOString(),
          precipitation: rainAmount
        });
      }
    }
    return of(data); // Return as an Observable
  }
}